// frontend/src/components/Layout/Sidebar.js
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink,  useLocation, matchPath } from 'react-router-dom'; // useParams removed as it's not used for projectId here
import { useAuth } from '../../contexts/AuthContext';
import { useProjectModal } from '../../contexts/ProjectModalContext';
import { useTasks, useTaskActions, useTaskStoreConnection } from '@modules/background-tasks';
import { ACTIVE_STATUSES } from '@modules/background-tasks';
import { useUsersPermissions } from '../../contexts/UsersPermissionsContext';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getProjectPath, getProjectMemberPath } from '../../utils/collectionPaths';

// MUI Imports
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Popper from '@mui/material/Popper';
import MenuList from '@mui/material/MenuList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TaskItem as NewTaskItem } from '@modules/background-tasks';

// Icons
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AppsIcon from '@mui/icons-material/Apps';
import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExtensionIcon from '@mui/icons-material/Extension';
import PeopleIcon from '@mui/icons-material/People';
import ScheduleIcon from '@mui/icons-material/Schedule';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { getUserProjects } from '../../services/userService';

const drawerWidth = 250;
const collapsedDrawerWidth = 60;

// Helper function to extract projectId from pathname
const getProjectIdFromPathname = (pathname) => {
  // Matches /project/ seguido por qualquer caractere que não seja '/'
  // e captura essa parte.
  const match = pathname.match(/^\/project\/([^/]+)/);
  return match ? match[1] : undefined;
};

function Sidebar({ mobileOpen, handleDrawerToggle, sidebarOpen, handleSidebarToggle }) {
  const { currentUser, isAdmin } = useAuth();
  const location = useLocation(); // Use useLocation to get the current path
  const { openCreateProjectModal } = useProjectModal();
  // Connect background-tasks store (legacy sidebar doesn't have useProject, use URL param)
  const projectIdFromUrl = location.pathname.match(/\/project\/([^/]+)/)?.[1];
  useTaskStoreConnection(projectIdFromUrl, currentUser?.uid);
  const runningTasksList = useTasks({ statusMode: 'active' });
  const { cancelTask, removeTask } = useTaskActions();
  const { openUsersPermissionsDialog } = useUsersPermissions();

  // Derive projectId from location.pathname
  const derivedProjectId = getProjectIdFromPathname(location.pathname);

  // 
  // 


  const [userProjects, setUserProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [currentProjectName, setCurrentProjectName] = useState('Projects'); // Initial sensible default
  const [currentUserRole, setCurrentUserRole] = useState(null); // Track current user's role in the project

  const [projectsDropdownOpen, setProjectsDropdownOpen] = useState(false);
  const projectsAnchorRef = useRef(null);
  const [projectsMenuAnchorEl, setProjectsMenuAnchorEl] = useState(null);

  // Helper function to close the projects dropdown
  const closeDropdown = () => {
    setProjectsDropdownOpen(false);
    setProjectsMenuAnchorEl(null);
  };

  const fetchProjectsForSidebar = async () => {
    if (!currentUser) {
      return;
    }
    try {
      // Get projects from the user's userProjects collection
      const userProjects = await getUserProjects(currentUser.uid);

      const projectList = userProjects.map((userProject) => ({
        id: userProject.id,
        ...userProject.project,
        role: userProject.role,
        // Ensure creationDate is a JS Date object for formatting
        creationDate: userProject.project.creationDate?.toDate
          ? userProject.project.creationDate.toDate()
          : userProject.project.creationDate || null,
      }));

      setUserProjects(projectList);
    } catch (err) {
      console.error("Error fetching projects for sidebar:", err);
      setUserProjects([]);
    }

    setLoadingProjects(false);
  };

  useEffect(() => {
    // Effect for fetching all user projects for the dropdown
    if (currentUser && currentUser.uid) { // Ensure currentUser.uid is available
      setLoadingProjects(true);
      fetchProjectsForSidebar();
    } else {
      setUserProjects([]);
    }
  }, [currentUser]); // Depends only on currentUser

  useEffect(() => {
    // Effect for fetching the current project's name based on derivedProjectId


    if (derivedProjectId && currentUser && currentUser.uid) {

      // setCurrentProjectName('Loading project...'); // Set a loading state

      const fetchProjectName = async () => {
        try {
          const projectPath = getProjectPath(derivedProjectId);

          const projectRef = doc(db, projectPath);
          const projectSnap = await getDoc(projectRef);

          if (projectSnap.exists()) {
            const projectData = projectSnap.data();

            if (projectData.projectName && projectData.projectName.trim() !== "") {
              setCurrentProjectName(projectData.projectName);

            } else {
              setCurrentProjectName('Unnamed Project');
              console.warn(`[Sidebar Project Name Effect] Project name field ("projectName") is missing, empty, or whitespace. Set to "Unnamed Project". Data received:`, projectData);
            }
          } else {
            console.warn(`[Sidebar Project Name Effect] Project document NOT FOUND at path: ${projectPath}. Setting to "Project Not Found".`);
            setCurrentProjectName('Project Not Found');
          }
        } catch (error) {
          console.error("[Sidebar Project Name Effect] Error fetching project name:", error);
          setCurrentProjectName('Error: Project Name');
        }
      };
      fetchProjectName();
    } else {
      if (!derivedProjectId) {

        setCurrentProjectName('Projects');
      } else if (!currentUser || !currentUser.uid) {

        setCurrentProjectName('Projects');
      }
    }
  }, [derivedProjectId, currentUser]); // Dependency array now uses derivedProjectId

  useEffect(() => {
    // Effect for fetching the current user's role in the project
    if (derivedProjectId && currentUser && currentUser.uid) {
      const fetchCurrentUserRole = async () => {
        try {
          const memberRef = doc(db, getProjectMemberPath(derivedProjectId, currentUser.uid));
          const memberSnap = await getDoc(memberRef);
          
          if (memberSnap.exists()) {
            const memberData = memberSnap.data();
            setCurrentUserRole(memberData.role);
          } else {
            setCurrentUserRole(null);
          }
        } catch (error) {
          console.error("[Sidebar User Role Effect] Error fetching user role:", error);
          setCurrentUserRole(null);
        }
      };
      fetchCurrentUserRole();
    } else {
      setCurrentUserRole(null);
    }
  }, [derivedProjectId, currentUser]);

  const handleOpenUsersPermissions = () => {
    if (derivedProjectId && currentProjectName && currentProjectName !== 'Projects' && currentProjectName !== 'Project Not Found' && currentProjectName !== 'Error: Project Name') {
      openUsersPermissionsDialog({
        id: derivedProjectId,
        projectName: currentProjectName
      });
    }
  };

  const handleProjectsToggle = () => {
    setProjectsDropdownOpen((prevOpen) => {
      if (!prevOpen) {
        setProjectsMenuAnchorEl(projectsAnchorRef.current);
      } else {
        setProjectsMenuAnchorEl(null);
      }
      return !prevOpen;
    });
  };

  const handleProjectsClose = (event) => {
    // Check if the click is outside the button and the menu
    if (projectsAnchorRef.current && projectsAnchorRef.current.contains(event.target)) {
      return; // Click was on the button, handleProjectsToggle will manage
    }
    // If projectsMenuAnchorEl exists and the click is within the Popper, don't close
    if (projectsMenuAnchorEl && projectsMenuAnchorEl.contains(event.target)) {
        // This condition might need refinement based on Popper's actual DOM structure.
        // For ClickAwayListener, it handles clicks outside its children.
        return;
    }
    setProjectsDropdownOpen(false);
    setProjectsMenuAnchorEl(null);
  };
  

  const handleProjectsListKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setProjectsDropdownOpen(false);
      setProjectsMenuAnchorEl(null);
    } else if (event.key === 'Escape') {
      setProjectsDropdownOpen(false);
      setProjectsMenuAnchorEl(null);
    }
  };


  const menuItems = [
    // Add Users & Permissions shortcut when inside a project and user is owner
    ...(derivedProjectId && currentUserRole === 'owner' ? [{
      text: 'Users & Permissions',
      icon: <PeopleIcon />,
      onClick: handleOpenUsersPermissions,
      isAction: true // Flag to indicate this is an action, not a navigation link
    }] : []),
    // Add Settings tab for admin users
    ...(isAdmin ? [{ text: 'Settings', icon: <SettingsIcon />, path: '/settings' }] : []),
    // Add Guides tab for admin users
    ...(isAdmin ? [{ text: 'Guides', icon: <MenuBookIcon />, path: '/guides' }] : []),
    // Add Users tab for admin users
    ...(isAdmin ? [{ text: 'Users', icon: <PeopleIcon />, path: '/users' }] : []),
    // Add Strategies Library tab for admin users
    ...(isAdmin ? [{ text: 'Strategies Library', icon: <ExtensionIcon />, path: '/strategies-library' }] : []),
    // Cron tasks admin (Sprint 20) — cross-project task observability + kill
    ...(isAdmin ? [{ text: 'Cron tasks', icon: <ScheduleIcon />, path: '/admin/cron-tasks' }] : []),
    ...(isAdmin ? [{ text: 'Provider Credentials', icon: <VpnKeyIcon />, path: '/admin/provider-credentials' }] : []),
  ];

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', padding:'5px', justifyContent:'space-between' }}>
      <Box>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarOpen ? 'space-between' : 'center',
        }}>
          {sidebarOpen && (
            <Button
              ref={projectsAnchorRef}
              id="projects-button"
              aria-controls={projectsDropdownOpen ? 'projects-menu' : undefined}
              aria-expanded={projectsDropdownOpen ? 'true' : undefined}
              aria-haspopup="true"
              onClick={handleProjectsToggle}
              endIcon={<ExpandMoreIcon />}
              fullWidth
              disableRipple
              sx={{
                textTransform: 'none',
                justifyContent: 'space-between',
                color: 'text.primary',
                fontWeight: 'bold',
                fontSize: '1.25rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
              }}
            >
              {currentProjectName}
            </Button>
          )}
          {/*<IconButton onClick={handleSidebarToggle} sx={{ display: { xs: 'none', sm: 'block' } }}>*/}
          {/*  {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}*/}
          {/*</IconButton>*/}
        </Box>

        {sidebarOpen && (
          <Popper
            open={projectsDropdownOpen}
            anchorEl={projectsMenuAnchorEl}
            role={undefined}
            placement="bottom-end"
            transition
            disablePortal={false}
            modifiers={[
              { name: 'offset', options: { offset: [0, 8] }},
              { name: 'preventOverflow', options: { boundary: 'clippingParents' }},
            ]}
            style={{ zIndex: 1300 }}
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
                }}
              >
                <Paper sx={{ width: Math.min(drawerWidth - 32, 300) , maxHeight: '60vh', overflow: 'auto' }}>
                  <ClickAwayListener onClickAway={handleProjectsClose}>
                    <MenuList
                      autoFocusItem={projectsDropdownOpen}
                      id="projects-menu"
                      aria-labelledby="projects-button"
                      onKeyDown={handleProjectsListKeyDown}
                    >
                      {derivedProjectId && currentProjectName && !['Projects', 'Project Not Found', 'Error: Project Name', 'Loading project...'].includes(currentProjectName) ? (
                        // STATE 1: A project is active - REORDERED
                        <>
                          {/* 1. Current Project Name (selected) */}
                          <MenuItem
                            key={`project-${derivedProjectId}`}
                            component={RouterLink}
                            to={`/project/${derivedProjectId}`}
                            onClick={closeDropdown}
                            selected={true}
                          >
                            <ListItemIcon><ArticleOutlinedIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary={currentProjectName} />
                          </MenuItem>

                          {/* 2. Divider */}
                          <Divider sx={{ my: 0.5 }} />

                          {/* 3. Other User Projects (list) */}
                          {loadingProjects ? (
                            <MenuItem disabled key="loading-other-projects">
                              <ListItemIcon><CircularProgress size={18} /></ListItemIcon>
                              <ListItemText primary="Loading projects..." />
                            </MenuItem>
                          ) : (
                            userProjects
                              .filter(project => project.id !== derivedProjectId)
                              .map((project) => (
                                <MenuItem
                                  key={project.id}
                                  component={RouterLink}
                                  to={`/project/${project.id}`}
                                  onClick={closeDropdown}
                                >
                                  <ListItemIcon><ArticleOutlinedIcon fontSize="small" /></ListItemIcon>
                                  <ListItemText primary={project.projectName} />
                                </MenuItem>
                              ))
                          )}
                          {!loadingProjects && userProjects.filter(p => p.id !== derivedProjectId).length === 0 && userProjects.length > 1 && (
                            <MenuItem disabled key="no-other-projects-available">
                              <ListItemText primary="No other projects" sx={{ pl: 5.5 }} />
                            </MenuItem>
                          )}

                          {/* 4. Divider (only if there were other projects or loading state for them) */}
                          {/* Or if no other projects but more than one project exists initially */} 
                          {(userProjects.filter(p => p.id !== derivedProjectId).length > 0 || loadingProjects || (userProjects.length > 1 && userProjects.filter(p => p.id !== derivedProjectId).length === 0)) && 
                            <Divider sx={{ my: 0.5 }} />
                          }

                          {/* 5. "All projects" */}
                          <MenuItem
                            key="all-projects-when-project-active"
                            component={RouterLink}
                            to="/all-projects"
                            onClick={closeDropdown}
                          >
                            <ListItemIcon><AppsIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="All projects" />
                          </MenuItem>

                          {/* 6. "Create new project" */}
                          <MenuItem
                            key="create-project-when-project-active"
                            component={RouterLink}
                            to={location.pathname} 
                            onClick={() => {
                              openCreateProjectModal();
                              closeDropdown(); 
                            }}
                          >
                            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Create new project" />
                          </MenuItem>
                        </>
                      ) : (
                        // STATE 2: No specific project is active (e.g., on dashboard or generic pages)
                        // Order remains: All Projects, Create New Project, ---, List of all projects
                        <>
                          <MenuItem
                            key="all-projects-when-no-project-active"
                            component={RouterLink}
                            to="/all-projects"
                            onClick={closeDropdown}
                            selected={location.pathname === "/all-projects" && !derivedProjectId}
                          >
                            <ListItemIcon><AppsIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="All projects" />
                          </MenuItem>
                          <MenuItem
                            key="create-project-when-no-project-active"
                            component={RouterLink}
                            to={location.pathname} // Stay on current page
                            onClick={() => {
                              openCreateProjectModal();
                              closeDropdown();
                            }}
                          >
                            <ListItemIcon><AddIcon fontSize="small" /></ListItemIcon>
                            <ListItemText primary="Create new project" />
                          </MenuItem>

                          {(loadingProjects || userProjects.length > 0) && <Divider sx={{ my: 0.5 }} />}

                          {loadingProjects ? (
                            <MenuItem disabled key="loading-projects-list-state2">
                              <ListItemIcon><CircularProgress size={18} /></ListItemIcon>
                              <ListItemText primary="Loading projects..." />
                            </MenuItem>
                          ) : userProjects.length === 0 ? (
                            <MenuItem disabled key="no-projects-list-state2">
                              <ListItemText primary="No projects yet" />
                            </MenuItem>
                          ) : (
                            userProjects.map((project) => (
                              <MenuItem
                                key={project.id}
                                component={RouterLink}
                                to={`/project/${project.id}`}
                                onClick={closeDropdown}
                              >
                                <ListItemIcon><ArticleOutlinedIcon fontSize="small" /></ListItemIcon>
                                <ListItemText primary={project.projectName} />
                              </MenuItem>
                            ))
                          )}
                        </>
                      )}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        )}

        <Divider />
        
        <List sx={{ flexGrow: 0 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ color: 'text.primary', margin: '0 0 3px 0' }}>
              <Tooltip title={sidebarOpen ? '' : item.text} placement="right">
                <ListItemButton 
                  selected={
                    !item.isAction &&
                    (item.matchPaths || [item.path]).some(pattern =>
                      matchPath({ path: pattern, end: false }, location.pathname)
                    )
                  }
                  onClick={item.isAction ? item.onClick : undefined}
                  component={item.isAction ? undefined : RouterLink}
                  to={item.isAction ? undefined : item.path}
                  sx={{ 
                    justifyContent: sidebarOpen ? 'initial' : 'center',  
                    padding:'4px 8px', 
                    borderRadius:'16px', 
                    }}
                >
                  <ListItemIcon sx={{ minWidth: '40px', justifyContent: 'center' }}>{item.icon}</ListItemIcon>
                  {sidebarOpen && <ListItemText 
                  primary={item.text} 
                  sx={{
                    '& .MuiTypography-root': {
                      fontWeight: 600,
                      fontSize: '14px',
                    },
                  }}
                  />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          ))}
        </List>

        <Divider/>
      </Box>

      <Box>
        {/* Tasks Section (background-tasks module) */}
        {runningTasksList.length > 0 && (
          <>
            <Divider />
            <Box
              sx={{
                maxHeight:'200px',
                overflow: 'hidden',
                overflowY:'auto',
              }}
            >
              <List
                sx={{
                  flexGrow: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                {sidebarOpen &&
                  runningTasksList.map((task) => (
                    <NewTaskItem
                      key={task.id}
                      task={task}
                      isUnviewed={false}
                      compact
                      onRemove={removeTask}
                      onCancel={cancelTask}
                    />
                  ))
                }
              </List>
            </Box>
          </>
        )}

        <Divider />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={handleSidebarToggle} sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {sidebarOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: sidebarOpen ? drawerWidth : collapsedDrawerWidth },
        flexShrink: { sm: 0 },
        transition: (theme) => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
      aria-label="mailbox folders"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, backgroundImage: 'none',  borderRadius: '0px' },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: sidebarOpen ? drawerWidth : collapsedDrawerWidth,
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            backgroundImage: 'none',
            borderRadius: '0px'
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
}

Sidebar.propTypes = {
  mobileOpen: PropTypes.bool.isRequired,
  handleDrawerToggle: PropTypes.func.isRequired,
  sidebarOpen: PropTypes.bool.isRequired,
  handleSidebarToggle: PropTypes.func.isRequired,
};

export default Sidebar;
